import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  askOllieFindingWhy,
  clearOllieThread,
  fetchOllieMessages,
  fetchOllieStarterPrompts,
  fetchOllieStatus,
  fetchOllieUsage,
  resetOllie,
  retryOllieSend,
  setPaywallRequested,
  sendOllieMessage,
  startOllieConversation,
  submitOllieFeedback,
} from 'src/features/OllieSlice'
import useOllieVenueId from 'src/hooks/useOllieVenueId'
import {
  OLLIE_ASK_CHIP_IDS,
  postOllieChip,
  postOllieReplyButton,
} from 'src/utils/ollieFindingsApi'

const selectOllie = state => state.ollie

const useOllie = () => {
  const dispatch = useDispatch()
  const ollie = useSelector(selectOllie)
  const { onVenueDashboard, venueId } = useOllieVenueId()

  const checkStatus = useCallback(
    () => dispatch(fetchOllieStatus()),
    [dispatch],
  )

  const loadStarterPrompts = useCallback(
    pageContext =>
      dispatch(
        fetchOllieStarterPrompts({
          venueId,
          pageContext,
        }),
      ),
    [dispatch, venueId],
  )

  const loadUsage = useCallback(
    () => (venueId ? dispatch(fetchOllieUsage(venueId)) : null),
    [dispatch, venueId],
  )

  const openConversation = useCallback(
    pageContext =>
      dispatch(
        startOllieConversation({
          venueId,
          pageContext,
        }),
      ),
    [dispatch, venueId],
  )

  /** Conversation about one finding (Why? / How? / Show me / Help me / Do it for me). */
  const openFindingConversation = useCallback(
    (checkId, pageContext = '') =>
      dispatch(
        startOllieConversation({
          venueId,
          pageContext,
          checkId,
        }),
      ),
    [dispatch, venueId],
  )

  const loadMessages = useCallback(
    (conversationId, pageContext = '') =>
      dispatch(
        fetchOllieMessages(
          conversationId || ollie.conversationId,
          pageContext,
          venueId,
        ),
      ),
    [dispatch, ollie.conversationId, venueId],
  )

  const sendMessage = useCallback(
    (message, pageContext = '') =>
      dispatch(
        sendOllieMessage({
          conversationId: ollie.conversationId,
          message,
          pageContext,
          venueId,
        }),
      ),
    [dispatch, ollie.conversationId, venueId],
  )

  /**
   * A finding chip. Why? / How? are sent as messages (free on Free while the
   * finding shows); the rest are recorded and return the action to run.
   */
  const sendChip = useCallback(
    (chip, pageContext = '') => {
      if (!chip?.id || !ollie.conversationId) return null
      if (OLLIE_ASK_CHIP_IDS.includes(chip.id)) {
        return dispatch(
          sendOllieMessage({
            conversationId: ollie.conversationId,
            message: chip.question || chip.label || '',
            pageContext,
            chipId: chip.id,
            venueId,
          }),
        )
      }
      return postOllieChip({
        conversationId: ollie.conversationId,
        chipId: chip.id,
        venueId,
      })
    },
    [dispatch, ollie.conversationId, venueId],
  )

  /** "Why?" on a card or noticed row: answer in the thread that just opened. */
  const askFindingWhy = useCallback(
    (start, pageContext = '') =>
      dispatch(askOllieFindingWhy({ start, pageContext, venueId })),
    [dispatch, venueId],
  )

  /** A button on one of Ollie's replies; returns the action to run. */
  const tapReplyButton = useCallback(
    (messageId, index) =>
      postOllieReplyButton({
        conversationId: ollie.conversationId,
        messageId,
        index,
        venueId,
      }),
    [ollie.conversationId, venueId],
  )

  const submitFeedback = useCallback(
    (messageId, rating, comment = '', reason = null) =>
      dispatch(
        submitOllieFeedback({
          conversationId: ollie.conversationId,
          messageId,
          rating,
          comment,
          reason,
        }),
      ),
    [dispatch, ollie.conversationId],
  )

  /** Ask the question Ollie didn't answer again. */
  const retrySend = useCallback(() => dispatch(retryOllieSend()), [dispatch])

  const reset = useCallback(() => dispatch(resetOllie()), [dispatch])

  const requestPaywall = useCallback(
    (open = true) => dispatch(setPaywallRequested(open)),
    [dispatch],
  )

  const clearThread = useCallback(
    () => dispatch(clearOllieThread()),
    [dispatch],
  )

  return {
    ...ollie,
    venueId,
    onVenueDashboard,
    isAvailable:
      ollie.enabled && ollie.ready && onVenueDashboard && Boolean(venueId),
    checkStatus,
    loadStarterPrompts,
    loadUsage,
    openConversation,
    openFindingConversation,
    loadMessages,
    sendChip,
    askFindingWhy,
    tapReplyButton,
    sendMessage,
    retrySend,
    submitFeedback,
    reset,
    requestPaywall,
    clearThread,
  }
}

export default useOllie
